// The signed-in account being moved to another cell (wiki/feature-home-region.md §7.5). The server
// says so on whoami and on every write it refuses; a push cannot, because a fence is not a read model.
// While it stands the store asks again on the server's own interval, which is how the session comes
// back after a freeze that outlasted its access token, and how an abort clears the banner.

import { get, writable, type Readable } from 'svelte/store';
import { ON_ADMIN_HOST } from '$lib/api/client';

const DEFAULT_RECHECK_SECONDS = 30;

export type AccountMove = { target: string | null };

const state = writable<AccountMove | null>(null);

export const accountMove: Readable<AccountMove | null> = { subscribe: state.subscribe };

let recheck: (() => Promise<void>) | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

export function setAccountMoveRecheck(fn: (() => Promise<void>) | null): void {
  recheck = fn;
}

function schedule(seconds: number): void {
  if (timer !== undefined) return;
  timer = setTimeout(() => {
    timer = undefined;
    void (recheck?.() ?? Promise.resolve())
      .catch(() => {})
      .finally(() => {
        if (get(state)) schedule(DEFAULT_RECHECK_SECONDS);
      });
  }, seconds * 1000);
}

// The admin plane answers to the operator, whose own product account moving says nothing about it.
export function noteAccountMoving(
  target: string | null | undefined,
  retryAfterSeconds: number = DEFAULT_RECHECK_SECONDS,
): void {
  if (ON_ADMIN_HOST) return;
  state.update((current) => ({ target: target ?? current?.target ?? null }));
  schedule(retryAfterSeconds);
}

export function noteAccountNotMoving(): void {
  state.set(null);
  clearTimeout(timer);
  timer = undefined;
}
